/* ============================================================================
   FACTURA-LOTE · selección y quitado en lote de las líneas de la factura.

   Están aquí, fuera del componente, porque son las reglas que hay que poder
   comprobar sin montar React: qué está seleccionado, cuánto suma, y qué
   atajos por tipo tienen sentido ofrecer.

   LA REGLA QUE LO ORDENA TODO (Trampa 2 del encargo): la selección es un
   `Set` de `idprocedimiento_cita_paciente`, NUNCA un array de filas atado al
   filtro visible. Escribir en el buscador no puede alterar lo seleccionado, y
   mucho menos lo que está en la factura.

   NINGUNA REGLA DE NEGOCIO VIVE AQUÍ. El importe se calcula con la MISMA
   fórmula que ya usaban la tabla y el modal de pendientes (precio × cantidad);
   no se inventa ni un redondeo.
   ========================================================================== */

/** Id canónico de una línea de la factura. Un solo sitio que lo decida. */
export function idDe(procedimiento) {
    return procedimiento?.idprocedimiento_cita_paciente;
}

/** Marca o desmarca un id. Devuelve un `Set` NUEVO (nunca muta el anterior). */
export function alternar(seleccion, id) {
    const siguiente = new Set(seleccion);
    if (siguiente.has(id)) siguiente.delete(id);
    else siguiente.add(id);
    return siguiente;
}

/**
 * "Seleccionar todo" sobre lo VISIBLE, no sobre la factura entera.
 *
 * Si con un filtro puesto ya están marcadas todas las visibles, la casilla de
 * la cabecera las DESMARCA -- y respeta lo que estuviera marcado fuera del
 * filtro. Sin esto, filtrar por "eco", marcar todo y luego limpiar el filtro
 * perdería silenciosamente la selección anterior.
 */
export function alternarTodas(seleccion, visibles) {
    const ids = visibles.map(idDe).filter((id) => id !== undefined && id !== null);
    const todasMarcadas = ids.length > 0 && ids.every((id) => seleccion.has(id));
    const siguiente = new Set(seleccion);
    ids.forEach((id) => (todasMarcadas ? siguiente.delete(id) : siguiente.add(id)));
    return siguiente;
}

/** Estado de la casilla de cabecera: ninguna, algunas o todas las visibles. */
export function estadoCabecera(seleccion, visibles) {
    const ids = visibles.map(idDe).filter((id) => id !== undefined && id !== null);
    if (ids.length === 0) return "ninguna";
    const marcadas = ids.filter((id) => seleccion.has(id)).length;
    if (marcadas === 0) return "ninguna";
    if (marcadas === ids.length) return "todas";
    return "algunas";
}

/**
 * Importe de lo seleccionado. MISMA fórmula que la tabla de la factura y que
 * el modal de pendientes: precio × cantidad, y una cantidad ausente vale 1.
 *
 * Un precio ausente suma 0, no NaN: en una barra que dice cuánto se va a
 * quitar, un "NaN" es peor que un cero.
 */
export function importeSeleccionado(procedimientos, seleccion) {
    return (procedimientos || []).reduce((acc, p) => {
        if (!seleccion.has(idDe(p))) return acc;
        const precio = Number(p?.precio);
        const cantidad = Number(p?.cantidad) || 1;
        return acc + (Number.isFinite(precio) ? precio * cantidad : 0);
    }, 0);
}

/** Las líneas seleccionadas, en el orden en el que están en la factura. */
export function seleccionadas(procedimientos, seleccion) {
    return (procedimientos || []).filter((p) => seleccion.has(idDe(p)));
}

/**
 * Limpia de la selección los ids que ya no están en la factura.
 *
 * Hace falta después de quitar, y también cuando se descarta la factura
 * entera: un `Set` con ids fantasma haría que la barra dijera "380
 * seleccionados" sobre una tabla vacía.
 */
export function podar(seleccion, procedimientos) {
    const vivos = new Set((procedimientos || []).map(idDe));
    const siguiente = new Set();
    seleccion.forEach((id) => {
        if (vivos.has(id)) siguiente.add(id);
    });
    return siguiente;
}

/* -- atajos por tipo -------------------------------------------------------
   El encargo pide "seleccionar solo los de laboratorio" y dice: sólo si la
   línea trae la categoría. NO la trae -- el `include` de pendientes es
   `ExamenTipo: true` sin anidar `categoria`. Lo que SÍ trae cada línea es
   `ExamenTipo.tipo` (LABORATORIO, RADIOLOGIA, ECOGRAFIA...), que es la lista
   cerrada de FTipoExamen.ts. Los atajos se construyen con eso.
   ------------------------------------------------------------------------- */

export function tipoDe(procedimiento) {
    const t = procedimiento?.ExamenTipo?.tipo;
    return typeof t === "string" && t.trim() !== "" ? t.trim() : null;
}

/**
 * Tipos presentes en la factura, con cuántas líneas tiene cada uno, de mayor
 * a menor. Sólo se ofrecen atajos que sirvan para algo: si todas las líneas
 * son del mismo tipo, el atajo sería idéntico a "seleccionar todo" y no se
 * ofrece.
 */
export function atajosPorTipo(procedimientos) {
    const cuenta = new Map();
    (procedimientos || []).forEach((p) => {
        const t = tipoDe(p);
        if (!t) return;
        cuenta.set(t, (cuenta.get(t) || 0) + 1);
    });
    if (cuenta.size < 2) return [];
    return [...cuenta.entries()]
        .map(([tipo, n]) => ({ tipo, n }))
        .sort((a, b) => b.n - a.n || a.tipo.localeCompare(b.tipo, "es"));
}

/** Ids de todas las líneas de un tipo. Sustituye a la selección, no la suma. */
export function idsDeTipo(procedimientos, tipo) {
    return new Set(
        (procedimientos || []).filter((p) => tipoDe(p) === tipo).map(idDe)
    );
}

/* -- confirmación ----------------------------------------------------------
   Por debajo del umbral se quita y se ofrece deshacer, que es más barato y
   más amable que preguntar. Por encima, se pregunta ADEMÁS de ofrecer
   deshacer: quitar 380 líneas de golpe merece un segundo de pausa.
   ------------------------------------------------------------------------- */
export const UMBRAL_CONFIRMACION = 20;

export function necesitaConfirmacion(cuantas) {
    return cuantas > UMBRAL_CONFIRMACION;
}
